/**
 * @component useHeaderState
 * @description 헤더 배경 상태, 메뉴 펼침 상태, 활성 네비게이션 키를 통합 관리하는 헤더 전용 훅입니다.
 * @usage Header 컴포넌트에서 경로와 스크롤/호버/모바일 메뉴 상태를 계산할 때 사용됩니다.
 */

import { useEffect, useMemo, useState } from 'react';
import { getSubNavKey, getTopLevelNavKey } from '../utils/routing';

export function useHeaderState(pathname) {
  const [isScrolled, setIsScrolled] = useState(false);    // 스크롤 내렸는지
  const [isHovered, setIsHovered] = useState(false);      // 헤더에 마우스 호버 중인지
  const [openMenu, setOpenMenu] = useState('');           // 모바일에서 열린 메뉴 키 (없으면 '')
  const [mobileOpen, setMobileOpen] = useState(false);    // 모바일 햄버거 메뉴 열림 상태

  // 스크롤 감지
  useEffect(() => {
    // 스크롤 Y 위치, 맨위 0 스크롤 내리면 양수
    // 스크롤 Y = 0  →  isScrolled = false  →  헤더 투명 / 스크롤 Y > 0  →  isScrolled = true   →  헤더 불투명
    const updateHeader = () => setIsScrolled(window.scrollY > 0);

    updateHeader();   // 처음 실행 시 현재 스크롤 위치 바로 반영

    // { passive: true } 는 "이 감시자는 스크롤을 막지 않을게" 라는 브라우저에 대한 약속
    // 스크롤 이벤트는 성능에 민감하기 때문에 이렇게 설정하면 브라우저가 최적화할 수 있다.
    window.addEventListener('scroll', updateHeader, { passive: true });

    return () => window.removeEventListener('scroll', updateHeader);
  }, []);

  // 페이지 이동 시 열려 있는 메뉴 상태를 초기화
  useEffect(() => {
    setOpenMenu('');      // 드롭다운 닫기
    setMobileOpen(false); // 모바일 메뉴 닫기
    setIsHovered(false);  // 호버 상태 해제
  }, [pathname]);         // pathname 바뀔 때마다 (= 페이지 이동할 때마다)

  const activeTopLevel = useMemo(() => getTopLevelNavKey(pathname), [pathname]);
  const activeSubLevel = useMemo(() => getSubNavKey(pathname), [pathname]);
  
  // 헤더 배경색 계산: 스크롤·호버·모바일 메뉴 중 하나라도 활성화되면 헤더를 불투명 상태로 전환
  const isSolid = isHovered || isScrolled || mobileOpen;
  const headerClassName = ['site-header', isSolid ? 'is-solid' : 'is-transparent'].filter(Boolean).join(' ');

  return {
    activeSubLevel,
    activeTopLevel,
    headerClassName,
    isHovered,
    isSolid,
    isScrolled,
    mobileOpen,
    openMenu,
    setIsHovered,
    setMobileOpen,
    setOpenMenu,
  };
}
